import { BCMSStateManagerCRUD, Branding, DisabledPrompt, Link, StateManager, useBCMSModal, useModel, useRoute, useStateManager } from "@bcm-dev/bcms-vite";
import Util from "classes/Util";
import React from "react";

import CustomModal, { CustomModalContent, ModalPrimaryButton, ModalSecondaryButton } from "components/custom-ui/CustomModal";
import CustomSelect, { CustomSelectItem } from "components/custom-ui/CustomSelect";
import { FormContainer, FormRowView } from "components/shared";
import { FormButtonRow } from "components/shared/containers/FormContainer";
import { UITextInput } from "components/ui";

import "./styles.css";
import "./mobile.css";


/**
 * @name FirstTimeLoginModal
 * @param {OBJECT} data - Data passed to the modal
 * @returns {React.Component}
 * @description The main component for the first time login modal
 */
export default function FirstTimeLoginModal(props) {
    const { close } = useBCMSModal();
    // Grab the data and ui from the props.data
    const { data: modalData, ui: modalUI } = props.data ?? {};
    // Set the data and ui variables with the default data and what was passed
    // through the properties
    const data = { ...getDefaultModalData(), ...modalData ?? {} };
    const ui = { ...getDefaultModalUI(), ...modalUI ?? {} };
    // The state that will be passed to the state manager
    const state = { data, ui };
    // :: React.state, React.setState -> OBJECT
    // Function that returns the controller for the state manager
    const controller = (state, setState) => ({
        data: state.data, // Set the data property in the controller
        ui: state.ui, // Set the ui property in the controller

        // setStep :: NUMBER -> VOID
        // Update the step property in the ui
        setStep: value => {
            BCMSStateManagerCRUD.init(state)
                .update([ "ui", "step" ], value)
                .submit(setState);
        },

        // onChange :: STRING -> * -> VOID
        // Update the data based on the passed prop and value parameters
        onChange: prop => value => {
            BCMSStateManagerCRUD.init(state)
                .update([ "data", prop ], value)
                .submit(setState);
        },

        onModalClose: evt => {
            if(Util.inMabi) {
                Util.routeMabi("login");
            } else {
                close("FirstTimeLoginModal");
            }
        }
    });

    return (
        <CustomModal title="Identity Verification" id="FirstTimeLoginModal" className="first-time-login-modal">
            <StateManager state={ state } controller={ controller }>
                <FirstTimeLoginContent />
            </StateManager>
        </CustomModal>
    )
}

/**
 * @name getDefaultModelData
 * @returns {OBJECT}
 * @description Hook to return the default data that the page requires
 */
export const getDefaultModalData = () => ({
    confirmationCode: ""
});

/**
 * @name getDefaultModalUI
 * @returns {OBJECT}
 * @description Hook to return the default ui that the page requires
 */
export const getDefaultModalUI = () => ({
    step: 0
});

/**
 * @name FirstTimeLoginContent
 * @returns {React.Component}
 * @description The main content component for the first time login modal
 */
function FirstTimeLoginContent(props) {
    const { data, ui } = useStateManager();

    return (
        <CustomModalContent>
            { ui.step === 0 && <GetConfirmationContent /> }
            { ui.step === 1 && <EnterConfirmationContent /> }
        </CustomModalContent>
    );
}

export function GetConfirmationContent(props) {
    const { setStep, ui, onModalClose } = useStateManager();
    const sendTextBtn = evt => setStep(ui.step + 1);

    return (
        <div className="get-confirmation-content">
            {/* START: Workflow Step 1 */}
            <p>As a security precaution, we need to verify your identity before you can proceed. This quick process helps us keep your account safe.</p>
            <p>Please choose one of the following options.</p>
            

            <FormContainer className="small-form-width long-short-buttons">
                <b data-point="Send_Me_A_Text_Message_Label">Send me a text message</b>
                <FormRowView className="button-row-follows" text="We will send you a text message with the following confirmation code.">
                <CustomSelect value="phone" dataPoint="Send_Text_To_Phone_Number_Select">
                    <CustomSelectItem value="phone" dataPoint="Send_Text_To_Phone_Number_Select_Item_0">(XXX)XXX-1234</CustomSelectItem>
                </CustomSelect>

                <ModalPrimaryButton text="Send Me A Text Message" dataPoint="Send_Text_Message_Button" onClick={ sendTextBtn } />
                </FormRowView>

                <b>Call my phone</b>

                <FormRowView className="button-row-follows" text="We will call you and provide a confirmation code.">
                <CustomSelect value="phone" dataPoint="Call_To_Phone_Number_Select">
                    <CustomSelectItem value="phone" dataPoint="Call_To_Phone_Number_Select_Item_0">(XXX)XXX-1234</CustomSelectItem>
                </CustomSelect>

                <DisabledPrompt>
                    <ModalPrimaryButton text="Call My Phone" />
                </DisabledPrompt>
                </FormRowView>

                <Branding id="email_verification_abi22" value="true" default="false">
                    <b>Email Verification</b>

                    <FormRowView className="button-row-follows" text="We will send you an email with a verification code.">
                    <p className="user-email-text">g*****w@sampleemail.com</p>
                    <DisabledPrompt>
                        <ModalPrimaryButton text="Email Verification" />
                    </DisabledPrompt>
                    </FormRowView>
                </Branding>
            </FormContainer>
            <div className="flex-container flex-horizontal align-center cant-be-reach-button-container">
                <Branding id="cant_be_reached_abi22" value="true" default="true">
                    <DisabledPrompt>
                        <ModalSecondaryButton text="I Can't Be Reached At Any Of These Numbers" />
                    </DisabledPrompt>
                </Branding>

                <ModalSecondaryButton text="Cancel" onClick={ onModalClose } />
            </div>
                
            
            <DisabledPrompt>
                <Link className="question-link">Why am I being asked this?</Link>
            </DisabledPrompt>
            {/* END: Workflow Step 1 */}
        </div>
    )
}

export function EnterConfirmationContent(props){
    const navigate = useRoute();
    const { data, onChange, onModalClose } = useStateManager();
    const {branding} = useModel();
    const options = branding.options;

    const onSubmit = evt => {
        // The "Remember This Device" page is skipped in MABI23.
        if(Util.inMabi) {
            Util.routeMabi("content/accountSummary");
        } else {
           options.Login_SaveDevice_1853 === "Yes" || options.Login_SaveDevice_1853 === "undefined" ? navigate("/firstTimeLogin") : navigate("/accountSummary");
        }
    };
  
    return(
      <div className="enter-confirmation-content">
        {/* START: Workflow Step 2 */}
        <p>We are sending a text message to the mobile number you selected. Please enter the code contained in the text message.</p>
        <FormContainer className="small-form-width">
          <FormRowView className="button-row-follows" text="* Confirmation Code">
            <UITextInput data-point="Enter_Confirmation_Code_Field" value={ data.confirmationCode } onChange={ onChange("confirmationCode") } />
          </FormRowView>
          <FormButtonRow>
            <ModalSecondaryButton text="Cancel" onClick={ onModalClose } />
            <ModalPrimaryButton text="Submit" onClick={ onSubmit } dataPoint="Submit_Confirmation_Code_Button" />
          </FormButtonRow>
          <FormRowView>
            <DisabledPrompt>
              <Link className="question-link">Why am I being asked this?</Link>
            </DisabledPrompt>
          </FormRowView>
        </FormContainer>
        {/* END: Workflow Step 2 */}
      </div>
    );
  }